import { MSOfficeMimeType } from '@aline/common';
import type { UUID } from '@aline/common';

import type { LookupDocxUploadDocument } from './resolve-docx-upload-target';
import { resolveDocxUploadTarget } from './resolve-docx-upload-target';
import type { UploadAsLatestVersionChoice } from './UploadAsLatestVersionDialog';

export interface VersionTargetFile {
  document: { id: UUID; name: string };
  file: File;
}

export interface SplitFilesByUploadTargetResult {
  newDocumentFiles: File[];
  versionTargetFiles: VersionTargetFile[];
}

// Classify before upload, reading bytes only for likely DOCX files.
export async function splitFilesByUploadTarget(options: {
  confirmUploadAsLatestVersion: (documentName: string) => Promise<UploadAsLatestVersionChoice>;
  files: File[];
  lookupDocument: LookupDocxUploadDocument;
}): Promise<SplitFilesByUploadTargetResult> {
  const { confirmUploadAsLatestVersion, files, lookupDocument } = options;
  const newDocumentFiles: File[] = [];
  const versionTargetFiles: VersionTargetFile[] = [];

  for (const file of files) {
    const bytes = getIsLikelyDocxFile(file) ? await file.arrayBuffer() : undefined;
    const target = await resolveDocxUploadTarget({
      bytes,
      filename: file.name,
      lookupDocument,
      mimetype: file.type,
    });
    const choice =
      target.kind === 'versionCandidate'
        ? await confirmUploadAsLatestVersion(target.document.name)
        : 'asNewDocument';
    if (target.kind === 'versionCandidate' && choice === 'asVersion')
      versionTargetFiles.push({ document: target.document, file });
    else newDocumentFiles.push(file);
  }

  return { newDocumentFiles, versionTargetFiles };
}

function getIsLikelyDocxFile(file: File): boolean {
  if (file.type === MSOfficeMimeType.DOCX) return true;

  return file.name.toLowerCase().endsWith('.docx');
}
